// src/components/InvGateSidebarActions.jsx
import React, { useState } from 'react';
import { Calendar, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Bell, Check, X, Clock, UserCheck, ChevronRight, FileText } from './icons';
import { useNavigate } from 'react-router-dom';

export default function InvGateSidebarActions({
  pendingApprovals = [],
  pendingTasks = [],
  dueSoonEvents = [],
  onResponderDerivacion,
  onCompleteTask
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('actions'); // 'actions' | 'participations'

  const totalPending = pendingApprovals.length + pendingTasks.length;

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* 1. Header con Tabs de Acciones Pendientes y Participaciones */}
      <div className="bg-white dark:bg-gray-850 rounded-xl border border-gray-200 dark:border-gray-750 p-1.5 flex items-center gap-1 shadow-xs">
        <button
          onClick={() => setActiveTab('actions')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-2.5 rounded-lg text-xs font-bold transition-all ${
            activeTab === 'actions'
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 shadow-2xs'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
          }`}
        >
          <span className="px-1.5 py-0.2 rounded-md bg-red-600 text-white text-[10px] font-extrabold">
            {totalPending}
          </span>
          <div className="flex flex-col text-left leading-tight">
            <span>Acciones pendientes</span>
            <span className="text-[9px] font-normal text-gray-500 opacity-80">Hacer, aprobar, completar</span>
          </div>
        </button>

        <button
          onClick={() => setActiveTab('participations')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-2.5 rounded-lg text-xs font-bold transition-all ${
            activeTab === 'participations'
              ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 shadow-2xs'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
          }`}
        >
          <Bell className="w-4 h-4 text-gray-400" />
          <div className="flex flex-col text-left leading-tight">
            <span>Participaciones</span>
            <span className="text-[9px] font-normal text-gray-500 opacity-80">Solicitudes en las que participo</span>
          </div>
        </button>
      </div>

      {/* 2. Lista de Tarjetas de Acciones */}
      {activeTab === 'actions' ? (
        <div className="space-y-3">
          {totalPending === 0 ? (
            <div className="bg-white dark:bg-gray-850 rounded-xl border border-gray-200 dark:border-gray-750 p-6 text-center text-xs text-gray-400">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2 opacity-80" />
              <p className="font-semibold text-gray-700 dark:text-gray-300">¡Al día!</p>
              <p className="text-[11px] mt-0.5">No tienes aprobaciones o tareas pendientes.</p>
            </div>
          ) : (
            <>
              {/* Tarjetas de Aprobación */}
              {pendingApprovals.map((appr, idx) => (
                <div
                  key={`appr-${idx}`}
                  className="bg-white dark:bg-gray-850 rounded-xl border border-gray-200 dark:border-gray-750 p-3.5 shadow-xs hover:shadow-md transition-shadow flex flex-col gap-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="text-xs font-bold text-gray-900 dark:text-white leading-tight">
                        {appr.titulo || 'Solicitud sin título'}
                      </h4>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
                        <UserCheck className="w-3 h-3 text-blue-500" />
                        <span>Aprobación en #{appr.id} ({appr.categoria || 'Soporte'})</span>
                      </p>
                    </div>

                    {/* Botones Rechazar / Aprobar */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onResponderDerivacion) onResponderDerivacion(e, appr.id, false);
                        }}
                        className="px-2.5 py-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors flex items-center gap-1"
                        title="Rechazar solicitud"
                      >
                        <X className="w-3 h-3" />
                        <span>Rechazar</span>
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onResponderDerivacion) onResponderDerivacion(e, appr.id, true);
                        }}
                        className="px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-md hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors flex items-center gap-1"
                        title="Aprobar solicitud"
                      >
                        <Check className="w-3 h-3" />
                        <span>Aprobar</span>
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-[10px] text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800">
                    <span>{appr.solicitante || 'Solicitante desconocido'}</span>
                    {appr.tiempo && <span>{appr.tiempo}</span>}
                  </div>
                </div>
              ))}

              {/* Tarjetas de Tareas */}
              {pendingTasks.map((task, idx) => (
                <div
                  key={`task-${idx}`}
                  className="bg-white dark:bg-gray-850 rounded-xl border border-gray-200 dark:border-gray-750 p-3.5 shadow-xs hover:shadow-md transition-shadow flex flex-col gap-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="text-xs font-bold text-gray-900 dark:text-white leading-tight">
                        {task.descripcion || 'Sin descripción'}
                      </h4>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
                        <FileText className="w-3 h-3 text-amber-500" />
                        <span>Tarea en #{task.id} ({task.categoria || 'Soporte General'})</span>
                      </p>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onCompleteTask) onCompleteTask(e, task.id);
                      }}
                      className="px-2.5 py-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-md hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors flex items-center gap-1 shrink-0"
                    >
                      <Check className="w-3 h-3" />
                      <span>Completar</span>
                    </button>
                  </div>

                  <div className="flex items-center justify-between text-[10px] text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800">
                    <span>Asignado: {task.asignadoA?.nombre ? `${task.asignadoA.nombre} ${task.asignadoA.apellido || ''}`.trim() : 'Sin asignar'}</span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-850 rounded-xl border border-gray-200 dark:border-gray-750 p-6 text-center text-xs text-gray-400">
          <Bell className="w-8 h-8 text-blue-500 mx-auto mb-2 opacity-80" />
          <p className="font-semibold text-gray-700 dark:text-gray-300">Participaciones Activas</p>
          <p className="text-[11px] mt-0.5">Tickets donde estás en copia o colaborando como especialista.</p>
        </div>
      )}

      {/* 3. Sección Due Soon / Próximos Eventos y Vencimientos */}
      <div className="bg-white dark:bg-gray-850 rounded-xl border border-gray-200 dark:border-gray-750 p-4 shadow-xs">
        <div className="flex items-center gap-2.5 pb-2.5 border-b border-gray-100 dark:border-gray-800">
          <span className="w-6 h-6 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center justify-center font-bold text-xs">
            {dueSoonEvents.length}
          </span>
          <div className="flex flex-col">
            <h4 className="text-xs font-bold text-gray-900 dark:text-white leading-tight">
              Por vencer
            </h4>
            <span className="text-[10px] text-gray-500 dark:text-gray-400">Próximos eventos y vencimientos SLA</span>
          </div>
        </div>

        <div className="mt-3 space-y-2.5 text-xs">
          {dueSoonEvents.length === 0 ? (
            <div className="py-2 text-center text-gray-400 text-[11px]">
              No hay eventos próximos a vencer.
            </div>
          ) : (
            dueSoonEvents.map((evt, idx) => (
              <div
                key={idx}
                onClick={() => evt.ticketId && navigate(`/tickets/${evt.ticketId}`)}
                className="flex items-start justify-between gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer group"
              >
                <div className="flex items-start gap-2 min-w-0">
                  <Calendar className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-[11px] font-medium text-gray-800 dark:text-gray-200 truncate group-hover:text-blue-600">
                      {evt.titulo}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      #{evt.ticketId} • {evt.descripcion}
                    </span>
                  </div>
                </div>
                {evt.tiempoRestante && (
                  <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 shrink-0">
                    {evt.tiempoRestante}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
